import { Button, cn } from "@growth/ui";
import { ArrowLeft, CircleAlert, CircleCheck, FileText, LoaderCircle } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { FullPageMessage } from "../components/full-page-message";
import { useRun } from "../lib/ai";
import { apiErrorMessage } from "../lib/api-error";

const STAGES = [
  { key: "research", label: "Research" },
  { key: "writing", label: "Writing" },
  { key: "critique", label: "Editor review" },
  { key: "visuals", label: "Charts & images" },
  { key: "cover", label: "Cover" },
  { key: "assembling", label: "Draft" },
];

const timeFormat = new Intl.DateTimeFormat("en-AU", {
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
});

export const AiRunPage = () => {
  const { id = "" } = useParams();
  const run = useRun(id);

  if (run.isPending) {
    return (
      <div className="flex justify-center py-24" aria-label="Loading run">
        <LoaderCircle className="size-6 animate-spin text-muted" />
      </div>
    );
  }
  if (run.isError) {
    return (
      <FullPageMessage icon={<CircleAlert className="size-5" />} title="Couldn't load this run">
        <p>{apiErrorMessage(run.error)}</p>
      </FullPageMessage>
    );
  }

  const data = run.data;
  const reached =
    data.status === "SUCCEEDED"
      ? STAGES.length
      : STAGES.findIndex((stage) => stage.key === data.stage);
  const lastStageBeforeFailure = data.events.filter((e) => e.stage !== "failed").at(-1)?.stage;
  const failedAt =
    data.status === "FAILED"
      ? STAGES.findIndex((stage) => stage.key === lastStageBeforeFailure)
      : -1;

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <Link to="/ai" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" />
        Write with AI
      </Link>
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted">AI run</p>
        <h1 className="font-serif text-3xl leading-tight text-ink">{data.title}</h1>
        <p className="text-sm text-muted">Estimated cost so far: US${data.costUsd.toFixed(2)}</p>
      </header>

      <ol className="grid grid-cols-3 gap-2 sm:grid-cols-6" aria-label="Progress">
        {STAGES.map((stage, index) => {
          const done = index < reached || data.status === "SUCCEEDED";
          const current = data.status === "RUNNING" && index === reached;
          const failed = index === failedAt;
          return (
            <li
              key={stage.key}
              aria-current={current ? "step" : undefined}
              className={cn(
                "flex flex-col gap-1.5 rounded-lg border px-3 py-2 text-xs",
                done && "border-emerald-200 bg-emerald-50 text-emerald-800",
                current && "border-accent/40 bg-blue-50 text-accent",
                failed && "border-red-200 bg-red-50 text-red-700",
                !done && !current && !failed && "border-line bg-white text-muted",
              )}
            >
              {done ? (
                <CircleCheck className="size-4" />
              ) : current ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : failed ? (
                <CircleAlert className="size-4" />
              ) : (
                <span className="size-4" />
              )}
              {stage.label}
            </li>
          );
        })}
      </ol>

      {data.status === "SUCCEEDED" && data.postId && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm text-emerald-900">
            The draft is ready. Review it, edit anything, then publish.
          </p>
          <Button asChild>
            <Link to={`/posts/${data.postId}`}>
              <FileText className="size-4" />
              Open the draft
            </Link>
          </Button>
        </div>
      )}
      {data.status === "FAILED" && (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          This run failed: {data.error}
        </p>
      )}

      <section aria-label="Activity" className="rounded-xl border border-line bg-white">
        <h2 className="border-b border-line px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted">
          Activity
        </h2>
        <ol className="divide-y divide-line" aria-live="polite">
          {data.events.map((event, index) => (
            <li key={index} className="flex gap-3 px-4 py-2.5 text-sm">
              <time className="w-20 shrink-0 text-xs tabular-nums text-muted" dateTime={event.at}>
                {timeFormat.format(new Date(event.at))}
              </time>
              <span className={event.stage === "failed" ? "text-red-700" : "text-ink"}>
                {event.message}
              </span>
            </li>
          ))}
          {data.status === "RUNNING" && (
            <li className="flex items-center gap-2 px-4 py-2.5 text-sm text-muted">
              <LoaderCircle className="size-3.5 animate-spin" /> Working… this usually takes 3–8
              minutes.
            </li>
          )}
        </ol>
      </section>
    </section>
  );
};
